import { KendraConst, SearchInterface } from "@amzn/ask-legal-domain";
import { Builder } from "builder-pattern";

type SearchQueryInput = SearchInterface.SearchQueryInput;

/** Any filter, sort or page size change starts again from page 1. */
export const withFirstPage = (query: SearchQueryInput): SearchQueryInput =>
    Builder<SearchQueryInput>(query).pageNumber(1).build();

const withoutAttribute = (query: SearchQueryInput, attribute: string): SearchQueryInput =>
    Builder<SearchQueryInput>(query)
        .filterConfiguration((query.filterConfiguration || []).filter(f => f.attribute !== attribute))
        .build();

/** All string values filtered on for an attribute, whether stored as one STRING_LIST entry or several STRING entries. */
export const getStringFilterValues = (query: SearchQueryInput, attribute: string): string[] =>
    (query.filterConfiguration || [])
        .filter(f => f.attribute === attribute)
        .reduce<string[]>((values, f) => {
            const value = f.valueMap.value;
            if (typeof value === "string") return [...values, value];
            if (Array.isArray(value)) return [...values, ...value];
            return values;
        }, []);

export const hasAttributeFilter = (query: SearchQueryInput, attribute: string): boolean =>
    (query.filterConfiguration || []).some(f => f.attribute === attribute);

/**
 * Replaces every filter on the attribute with one STRING "EqualsTo" filter per value
 * (the same shape the original filters build). No values means no filter.
 */
export const replaceStringFilters = (
    query: SearchQueryInput,
    attribute: string,
    values: string[]
): SearchQueryInput => {
    let updated = withoutAttribute(query, attribute);
    values.forEach(value => {
        updated = SearchInterface.SearchQueryInput.applyFilter(updated, {
            attribute,
            operation: "EqualsTo",
            valueMap: { value, valueType: KendraConst.IndexAttributeType.STRING }
        });
    });
    return withFirstPage(updated);
};

/** Replaces the filter on the attribute with one STRING_LIST "ContainsAny" filter. No values means no filter. */
export const replaceStringListFilter = (
    query: SearchQueryInput,
    attribute: string,
    values: string[]
): SearchQueryInput => {
    let updated = withoutAttribute(query, attribute);
    if (values.length > 0) {
        updated = SearchInterface.SearchQueryInput.applyFilter(updated, {
            attribute,
            operation: "ContainsAny",
            valueMap: { value: [...values], valueType: KendraConst.IndexAttributeType.STRING_LIST }
        });
    }
    return withFirstPage(updated);
};

/** Replaces the filter on the attribute with a "GreaterThanOrEquals" DATE filter. No date means no filter. */
export const replaceMinDateFilter = (
    query: SearchQueryInput,
    attribute: string,
    minDate?: Date
): SearchQueryInput => {
    let updated = withoutAttribute(query, attribute);
    if (minDate) {
        updated = SearchInterface.SearchQueryInput.applyFilter(updated, {
            attribute,
            operation: "GreaterThanOrEquals",
            valueMap: { value: minDate.toISOString(), valueType: KendraConst.IndexAttributeType.DATE }
        });
    }
    return withFirstPage(updated);
};

export const isSameValueSet = (a: string[], b: string[]): boolean =>
    a.length === b.length && a.every(value => b.includes(value));

export const MS_IN_DAY = 24 * 60 * 60 * 1000;
export const ANY_TIME = "any";

export interface LastUpdatedPeriod {
    value: string;
    label: string;
    days: number;
}

export const LAST_UPDATED_PERIODS: LastUpdatedPeriod[] = [
    { value: "week", label: "In the last week", days: 7 },
    { value: "twoweeks", label: "In the last two weeks", days: 14 },
    { value: "month", label: "In the last month", days: 30 },
    { value: "year", label: "In the last year", days: 365 }
];

/**
 * The query stores an absolute cut-off date, not a period, so this reverse-maps the date to the
 * NEAREST period. It is approximate: a filter restored from a shared URL whose cut-off sits near a
 * boundary can display the neighbouring period. Display only; the stored date drives the search.
 */
export const getSelectedPeriod = (
    query: SearchQueryInput,
    attribute: string = KendraConst.ContainerIngestionKendraAttributes.Default.LastUpdatedDate,
    now: number = Date.now()
): string => {
    const filter = (query.filterConfiguration || []).find(f => f.attribute === attribute);
    if (!filter) return ANY_TIME;
    const raw = filter.valueMap.value;
    const cutoff = Array.isArray(raw) ? NaN : new Date(raw).getTime();
    if (isNaN(cutoff)) return ANY_TIME;
    const days = (now - cutoff) / MS_IN_DAY;
    return LAST_UPDATED_PERIODS.reduce((closest, p) =>
        Math.abs(p.days - days) < Math.abs(closest.days - days) ? p : closest
    ).value;
};

/**
 * Active filters as lines for the chat agent's silent prompt, in the order of the Filters bar, e.g.
 * "Teams: aws-legal", "Page library: lib-1". Deliberately RAW IDS, not display names: the chat harness
 * takes instance / page / page-library ids as arguments of its MCP tool calls, so the prompt must carry
 * them (the Filters bar itself shows the human-readable names). Sort and paging are not filters.
 */
export const describeSearchFilters = (query: SearchQueryInput): string[] => {
    const attributes = KendraConst.ContainerIngestionKendraAttributes;
    const lines: string[] = [];

    const teams = getStringFilterValues(query, attributes.Default.InstanceId);
    if (teams.length) lines.push(`Teams: ${teams.join(", ")}`);
    const authors = getStringFilterValues(query, attributes.Default.Authors);
    if (authors.length) lines.push(`Authors: ${authors.join(", ")}`);
    const fileTypes = getStringFilterValues(query, attributes.Custom.ContentType);
    if (fileTypes.length) lines.push(`File type: ${fileTypes.join(", ")}`);
    const libraries = getStringFilterValues(query, attributes.Custom.PageLibraryId);
    if (libraries.length) lines.push(`Page library: ${libraries.join(", ")}`);
    const period = getSelectedPeriod(query);
    if (period !== ANY_TIME) {
        lines.push(`Last updated: ${LAST_UPDATED_PERIODS.find(p => p.value === period)?.label ?? period}`);
    }
    if (query.showStrictlyLegalOnly === true) lines.push("Legal only: show only legal results");
    if (query.showStrictlyLegalOnly === false) lines.push("Legal only: exclude legal results");
    return lines;
};

/**
 * Stable identity of the active filters, built from the raw filter entries (attribute, operation,
 * value) and the legal-only toggle, independent of entry order, sort and paging. The prompt lines from
 * describeSearchFilters are wording, not identity; this key is what decides whether a search is new.
 */
export const getSearchFilterKey = (query: SearchQueryInput): string => {
    const entries = (query.filterConfiguration || [])
        .map(f => {
            const raw = f.valueMap.value;
            // Arrays are order-independent; anything else is serialised so distinct values never collide
            const value = Array.isArray(raw) ? [...raw].sort().join("|") : JSON.stringify(raw ?? null);
            return `${f.attribute}:${f.operation}:${value}`;
        })
        .sort();
    return JSON.stringify([entries, query.showStrictlyLegalOnly ?? null]);
};
